/**
 * Distance-based LOD helper for heavy GLB assets in the floating-origin renderer.
 *
 * Because the camera always sits at the render origin (0,0,0) and every heavy
 * asset's group.position IS the floating-origin delta (worldPos − shipWorldPos),
 * the camera-distance to any asset is exactly group.position.length().  We toggle
 * three representations per asset per frame:
 *
 *   NEAR   (dist < FULL_KM)    — full high-poly GLB visible, proxy hidden
 *   MID    (dist < PROXY_KM)   — full hidden, cheap proxy visible
 *   FAR    (dist >= PROXY_KM)  — both hidden (sub-pixel; no GPU cost)
 *
 * All thresholds are NAMED CONSTANTS in km — easy to tune in one place.
 * The helper is pure presentation; it never touches sim-core or game logic.
 */
import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Threshold constants (km) — tune here.
// ---------------------------------------------------------------------------

/**
 * Station LOD thresholds.
 * Display radius = STATION_DISPLAY_KM = 8 km.
 * FULL: within ~18× display radius → see full GLB detail close up.
 * PROXY: within ~250× display radius → proxy torus readable from approach.
 * Beyond PROXY_STATION_KM both are hidden (station is truly far-field).
 */
export const FULL_STATION_KM = 150;   // ~18× display radius
export const PROXY_STATION_KM = 2_000; // proxy visible from supercruise approach

/**
 * Derelict hull LOD thresholds.
 * Display radius = DERELICT_DISPLAY_KM = 20 km.
 * FULL: within ~10× display radius → detail visible during combat pocket.
 * PROXY: within ~150× display radius → rough silhouette readable when coasting in.
 */
export const FULL_DERELICT_KM = 200;  // ~10× display radius
export const PROXY_DERELICT_KM = 3_000;

/**
 * Cenotaph LOD thresholds.
 * Display radius = RADIUS_KM = 70 km (monument is enormous).
 * The full GLB is heavy (~58 MB); hide it beyond reasonable encounter distance.
 * FULL: within ~5× display radius — up close, seeing inscriptions.
 * PROXY: within ~30× display radius — silhouette readable from far approach.
 * The procedural fallback sphere is intentionally exempt: it is always controlled
 * by the caller (cenotaph.ts) and is NOT passed here as `full`.
 */
export const FULL_CENOTAPH_KM = 350;  // ~5× display radius
export const PROXY_CENOTAPH_KM = 2_100; // ~30× display radius

// ---------------------------------------------------------------------------
// Core LOD helper
// ---------------------------------------------------------------------------

/**
 * Apply distance-based LOD to one asset group each frame.
 *
 * @param groupPos   The render-space position of the parent group
 *                   (group.position — already IS the camera distance vector).
 * @param full       The heavy Object3D (loaded GLB scene). May be null if not
 *                   yet loaded — in that case only the proxy is toggled.
 * @param proxy      A cheap stand-in mesh (low-seg primitive, pre-sized to the
 *                   display radius).
 * @param fullKm     Distance threshold below which `full` is shown (km).
 * @param proxyKm    Distance threshold below which `proxy` is shown (km).
 *                   Must be >= fullKm.
 */
export function applyLod(
  groupPos: THREE.Vector3,
  full: THREE.Object3D | null,
  proxy: THREE.Object3D,
  fullKm: number,
  proxyKm: number,
  glint?: THREE.Sprite | null,
): void {
  const dist = groupPos.length();

  if (dist < fullKm) {
    // NEAR — show full detail, hide proxy
    if (full !== null) full.visible = true;
    proxy.visible = false;
  } else if (dist < proxyKm) {
    // MID — hide full, show cheap proxy
    if (full !== null) full.visible = false;
    proxy.visible = true;
  } else {
    // FAR — both hidden; a distance-driven glint stands in so the asset never
    // vanish-pops off the proxy edge (see updateGlint).
    if (full !== null) full.visible = false;
    proxy.visible = false;
  }
  if (glint) updateGlint(glint, dist, proxyKm);
}

// ---------------------------------------------------------------------------
// FAR-LOD glint sprite
// ---------------------------------------------------------------------------

/**
 * Once an asset passes PROXY_*_KM both its full mesh and proxy are hidden, so it
 * used to blink out mid-approach. A single additive sun-tinted point sprite fades
 * IN across that boundary and persists — a distant object catching the sun, never
 * a hard pop. Kept at a constant angular size so it reads at any range.
 */

/** Warm sun-glint tint (a hot white with a faint amber core), matte-palette safe. */
const GLINT_TINT = 0xfff2d8;
/** Peak glint opacity (additive; deliberately restrained — a glint, not a flare). */
const GLINT_MAX_OPACITY = 0.85;
/** Floor fraction of the peak the glint carries the INSTANT the proxy turns off, so
 * the asset is immediately a visible mote (never a vanish) rather than fading from 0. */
const GLINT_FLOOR = 0.35;
/** Screen-constant size: sprite world-scale = dist × this, so it holds ~angular px. */
const GLINT_SCREEN_FACTOR = 0.012;

/** Pure glint state for a given camera distance: hidden inside the proxy band, then —
 * the instant the proxy turns off — a mote at GLINT_FLOOR that ramps to full over the
 * next 25% of the proxy threshold and holds. It is never invisible past the boundary. */
export interface GlintState {
  visible: boolean;
  opacity: number;
}

export function computeGlint(
  dist: number,
  proxyKm: number,
  maxOpacity: number = GLINT_MAX_OPACITY,
): GlintState {
  if (dist < proxyKm) return { visible: false, opacity: 0 };
  const ramp = Math.min(1, (dist - proxyKm) / (proxyKm * 0.25));
  return { visible: true, opacity: maxOpacity * (GLINT_FLOOR + (1 - GLINT_FLOOR) * ramp) };
}

/** A reusable additive sun-glint sprite (no texture — the soft round default sprite
 * point reads as a glint). Callers add it to the asset group and pass it to applyLod. */
export function createGlintSprite(tint: number = GLINT_TINT): THREE.Sprite {
  const material = new THREE.SpriteMaterial({
    color: tint,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const sprite = new THREE.Sprite(material);
  sprite.visible = false;
  return sprite;
}

/** Drive a glint sprite from the camera distance: fade + hold per computeGlint, and
 * scale to a constant angular size so a far station/derelict is always a visible mote. */
export function updateGlint(glint: THREE.Sprite, dist: number, proxyKm: number): void {
  const state = computeGlint(dist, proxyKm);
  glint.visible = state.visible;
  if (!state.visible) return;
  (glint.material as THREE.SpriteMaterial).opacity = state.opacity;
  glint.scale.setScalar(dist * GLINT_SCREEN_FACTOR);
}
